import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';

import useI18n from '../../common/hooks/useI18n';

import ImageCarousel from './imageCarousel';

type TimelineEvent = {
  year: number;
  month?: number;
  i18nKey: string;
  imagesCount: number;
};

type Props = {
  title: string;
  i18nPrefix: string;
  events: TimelineEvent[];
};

const Container = styled.div`
  width: 100%;
  min-height: 300px;
  padding: 32px 16px;
  background-color: ${(props) => props.theme.colors.primary};
  color: white;
`;

const Title = styled.h2`
  ${(props) => props.theme.font.h2};
  text-align: center;
  margin-bottom: 64px;
  color: white;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-bottom: 32px;
  }
`;

const TimelineContainer = styled.div`
  position: relative;
  max-width: 1024px;
  max-width: min(1024px, 84vw);
  margin: 0 auto;
`;

const YearsRow = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
  margin-left: -40px;
  margin-right: -40px;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-left: -24px;
    margin-right: -24px;
  }
`;

const Year = styled.span`
  width: 160px;
  font-size: 18px;
  text-align: center;

  @media ${(props) => props.theme.breakpoints.smd} {
    width: 96px;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    font-size: 14px;
  }
`;

const Month = styled.span`
  @media ${(props) => props.theme.breakpoints.smd} {
    display: none;
  }
`;

const TimelineBar = styled.div`
  position: relative;
`;

const Line = styled.div`
  position: absolute;
  top: 50%;
  left: 0;
  right: 0;
  height: 2px;
  background-color: #ef8f8b;
  transform: translateY(-50%);
`;

const DotsContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  margin-left: 24px;
  margin-right: 24px;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-left: 12px;
    margin-right: 12px;
  }
`;

const Dot = styled.button<{ isSelected: boolean }>`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  transition: all 300ms;
  position: relative;
  z-index: 10;
  background-color: ${(props) => (props.isSelected ? 'white' : '#ef8f8b')};
  transform: ${(props) => (props.isSelected ? 'scale(1.25)' : 'scale(1)')};
  cursor: pointer;
  border: none;
  padding: 0;
`;

const Content = styled.div`
  margin-top: 48px;
  display: flex;
  align-items: center;
  gap: 32px;
  flex-wrap: wrap;

  @media ${(props) => props.theme.breakpoints.smd} {
    margin-top: 32px;
    gap: 16px;
  }
`;

const ContentTitle = styled.div`
  flex: 1;
  ${(props) => props.theme.font.h2};
  line-height: 100% !important;
  font-weight: 600 !important;
  color: white;
  text-align: right;

  @media ${(props) => props.theme.breakpoints.mdd} {
    font-size: 18px;
  }

  @media ${(props) => props.theme.breakpoints.smd} {
    flex-basis: 100%;
    width: 100%;
    text-align: left;
  }

  @media ${(props) => props.theme.breakpoints.xs} {
    font-size: 14px;
  }
`;

const ImageContainer = styled.div`
  margin-left: auto;
  margin-right: auto;
  margin-top: 32px;
  margin-bottom: 32px;
  max-width: min(50vw, 675px);
  aspect-ratio: 4 / 3;

  @media ${(props) => props.theme.breakpoints.xs} {
    max-width: unset;
    width: 100%;
    margin-bottom: 0;
  }
`;

const ContentText = styled.p`
  ${(props) => props.theme.font.p1};
  flex: 3;
`;

const HorizontalTimeline: React.FC<Props> = ({ title, i18nPrefix, events }) => {
  const i18n = useI18n();

  const [selectedIndex, setSelectedIndex] = useState(0);

  const imageUrls = useMemo(() => {
    const selectedEvent = events[selectedIndex];
    return Array.from(
      { length: selectedEvent.imagesCount },
      (_, index) =>
        `/images/timeline/${i18nPrefix}/${events[selectedIndex].i18nKey}${
          events[selectedIndex].imagesCount > 1 ? '_' + index : ''
        }.jpg`
    );
  }, [events, i18nPrefix, selectedIndex]);

  const getMonthAsString = useCallback(
    (month: number) => {
      const monthString = new Date(0, month, 1).toLocaleString(
        i18n.activeLocale,
        {
          month: 'long',
        }
      );
      return monthString.charAt(0).toUpperCase() + monthString.slice(1);
    },
    [i18n.activeLocale]
  );

  const handleDotClick = useCallback((index: number) => {
    setSelectedIndex(index);
  }, []);

  if (events.length === 0) {
    return null;
  }

  return (
    <Container>
      <Title>{title}</Title>

      <TimelineContainer>
        <YearsRow>
          {events.map((event, index) => (
            <Year as="span" key={`year-${index}`}>
              {typeof event.month === 'number' && (
                <>
                  <Month>{getMonthAsString(event.month)}</Month>{' '}
                </>
              )}
              {event.year}
            </Year>
          ))}
        </YearsRow>

        <TimelineBar>
          <Line />

          <DotsContainer>
            {events.map((event, index) => (
              <Dot
                key={`dot-${index}`}
                as="button"
                onClick={() => handleDotClick(index)}
                isSelected={selectedIndex === index}
                aria-label={`View event from ${event.year}`}
              />
            ))}
          </DotsContainer>
        </TimelineBar>

        <Content>
          <ContentTitle>
            {typeof events[selectedIndex].month === 'number' && (
              <>{getMonthAsString(events[selectedIndex].month)} </>
            )}
            {events[selectedIndex].year}
          </ContentTitle>
          <ContentText>
            {i18n.t(`${i18nPrefix}.${events[selectedIndex].i18nKey}`)}
          </ContentText>
        </Content>

        <ImageContainer>
          <ImageCarousel pictureUrls={imageUrls} />
        </ImageContainer>
      </TimelineContainer>
    </Container>
  );
};

export default HorizontalTimeline;
